import dayjs from "dayjs";
import { useSearchParams } from "react-router";
import styled from "styled-components";
import CalendarDate from "../../../../../Components/CalendarDate";
import { useCalendarStore } from "../../store/useCalendarStore";
import { Avatar, Button, Tooltip } from "@heroui/react";
import { useShallow } from "zustand/react/shallow";
import { useTheme } from "next-themes";
import { spansDate } from "../hooks/dayAbsence";
import { StaffHeaderCell } from "./StaffHeaderCell";

const NONE = [];

const DayName = ({ day }) => {
  const {
    dateActions,
    calendarMode,
    resources,
    calendarDays,
    timeoffs,
    allShifts,
    showNames,
  } = useCalendarStore(
    useShallow((state) => ({
      dateActions: state.dateActions,
      calendarMode: state.calendarMode,
      resources: state.resources,
      calendarDays: state.calendarData.days || NONE,
      timeoffs: state.timeoffs?.timeoffs ?? NONE,
      allShifts: state.calendarData.allShifts ?? NONE,
      showNames: state.filterItems.showStaffNames,
    })),
  );

  const [params, setParams] = useSearchParams();

  const { theme, resolvedTheme } = useTheme();
  const activeTheme = theme === "system" ? resolvedTheme : theme;

  const userColumnWidthPx = 80;
  const isOnlyDay = calendarDays.length === 1;
  const usersToRender = day.users.filter(
    (u) => !params.get("u") || u.userId === Number(params.get("u")),
  );
  const resourcesToRender = resources.filter(
    (r) =>
      r.isEnabledInFilter &&
      (!params.get("r") || r.id === Number(params.get("r"))),
  );
  // Every day reserves the badge row once any has one, so avatars stay level.
  const reservesBadgeSlot = timeoffs.some(
    (row) => row.userId != null && row.typeGranularity === "DAY",
  );
  const dayRowsByUser = groupByUser(
    timeoffs.filter(
      (row) =>
        row.typeGranularity === "DAY" &&
        spansDate(row.timeFrom, row.timeTo, day.date),
    ),
  );
  const shiftsByUser = groupByUser(
    allShifts.filter((shift) => spansDate(shift.start, shift.end, day.date)),
  );

  const fixedDayWidthPx =
    calendarMode === "users" && !isOnlyDay
      ? (params.get("u")
          ? 1
          : Math.max(
              1,
              ...calendarDays.map((calendarDay) => calendarDay.users.length),
            )) * userColumnWidthPx
      : undefined;

  return (
    <Wrapper
      style={{ display: "flex" }}
      isDark={activeTheme === "dark"}
      fixedDayWidthPx={fixedDayWidthPx}
    >
      <div
        style={{
          paddingTop: "5px",
          margin: "0 auto",
          position: "sticky",
          top: "0",
          background: activeTheme === "dark" ? "#121212" : "white",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <div className="flex w-full flex-col items-center">
          {day.isHoliday && (
            <Tooltip
              content={day.isHoliday}
              label={day.isHoliday}
              showArrow
              placement="bottom"
              classNames={{
                content: "bg-[#7e96cd] text-white ",
                arrow: "bg-[#7e96cd] before:bg-[#7e96cd]",
              }}
            >
              <div className="holiday_span">{day.isHoliday}</div>
            </Tooltip>
          )}
          <CalendarDate
            date={day.date}
            onClick={() =>
              dateActions.setSelectedDateAndView(dayjs(day.date), "day")
            }
          />
        </div>
      </div>
      {calendarMode === "users" && (
        <div className={"users"}>
          {usersToRender.length > 0 ? (
            usersToRender.map((user) => {
              return (
                <div className="avatarCell" key={user.userId}>
                  <StaffHeaderCell
                    user={user}
                    date={day.date}
                    rows={dayRowsByUser.get(user.userId) ?? NONE}
                    shifts={shiftsByUser.get(user.userId) ?? NONE}
                    reservesBadgeSlot={reservesBadgeSlot}
                  />
                </div>
              );
            })
          ) : (
            <div className="avatarCell">
              <LimeAvatar user={{ name: "Ni zaposlenih", image: null }} />
            </div>
          )}
        </div>
      )}

      {calendarMode === "resources" && (
        <div className="resources">
          {resourcesToRender.map((resource) => (
            <div className="avatarCell" key={resource.id}>
              <div className="flex w-full min-w-0 flex-col items-center gap-0.5">
                <LimeAvatar
                  user={{ name: resource.label, image: resource.imageUrl }}
                  onClick={() => {
                    setParams((prev) => {
                      if (prev.has("r")) {
                        prev.delete("r");
                      } else {
                        prev.set("r", String(resource.id));
                      }
                      return prev;
                    });
                  }}
                />
                {showNames && resource.label ? (
                  <p
                    className="w-full truncate px-1 text-center font-medium text-[11px] text-default-700 leading-tight contain-inline-size"
                    data-identifier="calendar-resource-name"
                  >
                    {resource.label}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Wrapper>
  );
};

function groupByUser(items) {
  const byUser = new Map();
  for (const item of items) {
    if (item.userId == null) continue;
    const own = byUser.get(item.userId);
    if (own) own.push(item);
    else byUser.set(item.userId, [item]);
  }
  return byUser;
}

const LimeAvatar = ({ user, onClick }) => {
  return (
    <Tooltip
      placement="bottom"
      showArrow
      content={
        <div className="flex max-w-75 flex-col items-center">
          <p className="font-medium">{user?.name}</p>
          {user.description ? (
            <p className="text-secondary-text text-sm">{user.description}</p>
          ) : null}
        </div>
      }
    >
      <Button
        variant="light"
        onPress={onClick}
        className="shrink-0 cursor-pointer data-[hover=true]:bg-transparent"
        disableRipple
      >
        <div className="flex items-center justify-center">
          <Avatar
            name={user?.name}
            src={user?.image}
            getInitials={(n) =>
              n
                .split(" ")
                .map((word) => word[0])
                .join("")
                .slice(0, 2)
            }
            showFallback
            color="primary"
            classNames={{
              base: "bg-[#ebf8ef] cursor-pointer shrink-0 w-[34px] h-[34px]",
              icon: "text-[#74b587]",
              name: "text-[#74b587] font-semibold",
            }}
          />
        </div>
      </Button>
    </Tooltip>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
  flex: 1;

  justify-content: end;

  gap: 10px;

  border-right: 2px dashed transparent;

  &:not(:last-child) {
    border-right: 2px dashed #e3e3e3;
  }

  .holiday_span {
    height: 20px;
    line-height: 20px;
    font-size: 12px;
    background-color: #7e96cd;

    line-break: anywhere;

    color: #fafafa;
    width: 100%;
    border-radius: 12px;

    display: block;
    justify-content: left;
    align-items: center;
    padding: 0 15px;

    cursor: default;
  }

  .users,
  .resources {
    display: flex;
    width: 100%;

    .avatarCell {
      min-width: 80px;
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .userName {
      display: block;
      height: 45px;

      flex: 1;

      text-overflow: ellipsis;
      overflow: hidden;
      line-break: anywhere;

      cursor: default;
      font-size: 0.8rem;

      font-weight: 500;
      color: #6c7386;

      background: ${({ isDark }) => (isDark ? "#121212" : "white")};

      min-width: 80px;

      @media (min-width: 768px) {
        font-size: 0.75rem;
      }
    }
  }

  @media (min-width: 768px) {
    gap: 5px;
  }

  @media (max-width: 768px) {
    width: ${({ fixedDayWidthPx }) =>
      fixedDayWidthPx != null ? `${fixedDayWidthPx}px` : "auto"};
    min-width: ${({ fixedDayWidthPx }) =>
      fixedDayWidthPx != null ? `${fixedDayWidthPx}px` : "auto"};
    flex: ${({ fixedDayWidthPx }) =>
      fixedDayWidthPx != null ? "0 0 auto" : 1};
  }
`;

export default DayName;
